import React from "react";
import { ActionIcon, Box, Drawer, Group } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import styles from "./CommissionDrawerView.module.css";

export interface CommissionDrawerViewProps {
  title: React.ReactNode;
  /** Shows a back chevron before the title. */
  onBack?: () => void;
  /** Header buttons, left of the close button. */
  actions?: React.ReactNode;
  /** Tabs flush on the header's bottom rule. */
  tabs?: React.ReactNode;
  /** Pinned under the header, outside the scroll (e.g. a search box). */
  toolbar?: React.ReactNode;
  /** Draw the rule under the header. Defaults to true. */
  headerDivider?: boolean;
  /** Sticky footer (form buttons). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The layout of every Commission Rate drawer screen — rate details, the
 * add/edit form and "Rates by service". Renders inside the drawer, so the
 * title labels the dialog and the close button closes it.
 */
const CommissionDrawerView: React.FC<CommissionDrawerViewProps> = ({
  title,
  onBack,
  actions,
  tabs,
  toolbar,
  headerDivider = true,
  footer,
  children,
}) => (
  <Box className={styles.view}>
    <Box
      className={styles.header}
      data-with-tabs={tabs ? true : undefined}
      data-no-divider={headerDivider ? undefined : true}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap" className={styles.titleBlock}>
          {onBack && (
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Back"
              onClick={onBack}
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
          )}
          <Drawer.Title className={styles.title}>{title}</Drawer.Title>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {actions}
          <Drawer.CloseButton />
        </Group>
      </Group>
      {tabs && <Box className={styles.tabs}>{tabs}</Box>}
      {toolbar && <Box className={styles.toolbar}>{toolbar}</Box>}
    </Box>
    <Box className={styles.body}>{children}</Box>
    {footer && <Box className={styles.footer}>{footer}</Box>}
  </Box>
);

export default CommissionDrawerView;
